import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { AGE_STYLES, formatFee } from "@/lib/candidate-utils";
import {
  Users, CurrencyDollar, Bell, Clock, Trophy, ArrowRight, CheckCircle,
  FolderOpen, Crown, Calendar, EnvelopeSimple, Target, PencilSimple,
  UsersThree, X, Fire, PaperPlaneTilt, Warning, Check,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const KPI = ({ label, value, sub, icon: Icon, testId, accent }) => (
  <div className="bg-white border border-zinc-200 p-6 rounded-sm hover:border-zinc-300 transition-colors" data-testid={testId}>
    <div className="flex items-start justify-between mb-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <Icon size={18} weight="bold" className={accent ? "text-[#FF2A2A]" : "text-zinc-400"} />
    </div>
    <div className="font-heading font-black tracking-tight text-5xl leading-none">{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-3">{sub}</div>}
  </div>
);

const ACTION_META = {
  interview_today: { icon: Calendar, tint: "border-l-emerald-500 bg-emerald-50/40", label: "TODAY" },
  feedback_overdue: { icon: PaperPlaneTilt, tint: "border-l-[#FF2A2A] bg-red-50/40", label: "CHASE" },
  red: { icon: Fire, tint: "border-l-[#FF2A2A] bg-red-50/40", label: "RED" },
  needs_interviews: { icon: Bell, tint: "border-l-amber-500 bg-amber-50/40", label: "BOOK" },
  red_approaching: { icon: Warning, tint: "border-l-amber-500 bg-amber-50/40", label: "SOON" },
  no_activity: { icon: Clock, tint: "border-l-zinc-400 bg-zinc-50", label: "QUIET" },
};

function TodayActionsSection({ data, busyAction, onDismiss, onSendChase }) {
  const [filter, setFilter] = useState("all");
  if (!data) return null;
  const all = data.actions || [];
  const counts = data.counts || {};

  const filters = [
    { v: "all", label: "All", count: data.total },
    { v: "interview_today", label: "Today", count: counts.interview_today || 0 },
    { v: "feedback_overdue", label: "Chase", count: counts.feedback_overdue || 0 },
    { v: "red", label: "Red", count: counts.red || 0 },
    { v: "needs_interviews", label: "Book", count: counts.needs_interviews || 0 },
    { v: "no_activity", label: "Quiet", count: counts.no_activity || 0 },
  ];
  const visible = filter === "all" ? all : all.filter((a) => a.type === filter);

  return (
    <div className="bg-white border-2 border-zinc-950 rounded-sm p-6" data-testid="today-actions-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Dollar-productive actions</div>
          <h2 className="font-heading font-black text-2xl tracking-tight flex items-center gap-2">
            <Fire size={20} weight="fill" className="text-[#FF2A2A]" />
            Today's actions
            <span className="text-sm text-zinc-500 font-normal ml-1">· {data.total} to clear</span>
          </h2>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`today-filter-${f.v}`}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                filter === f.v
                  ? "border-zinc-950 bg-zinc-950 text-white"
                  : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
              }`}
            >
              {f.label} {f.count > 0 && <span className="ml-1 opacity-80">{f.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="text-sm text-zinc-500 py-10 text-center border border-dashed border-zinc-200 rounded-sm" data-testid="today-actions-empty">
          {data.total === 0 ? (
            <div className="flex flex-col items-center gap-2">
              <CheckCircle size={24} weight="fill" className="text-emerald-500" />
              <div className="font-medium text-zinc-950">Inbox zero — nothing to chase.</div>
              <div className="text-xs text-zinc-500">Come back tomorrow morning.</div>
            </div>
          ) : (
            "Nothing to show for this filter."
          )}
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="today-actions-list">
          {visible.map((a) => {
            const meta = ACTION_META[a.type] || { icon: Bell, tint: "border-l-zinc-300 bg-zinc-50", label: a.type };
            const Icon = meta.icon;
            const busy = busyAction === a.id;
            return (
              <li
                key={a.id}
                className={`flex items-start gap-3 py-3 pl-3 border-l-4 ${meta.tint}`}
                data-testid={`action-${a.type}`}
              >
                <Icon size={16} weight="bold" className="mt-0.5 text-zinc-700 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">{meta.label}</span>
                    <span className="text-sm font-medium text-zinc-950 truncate">{a.title}</span>
                  </div>
                  {a.description && (
                    <div className="text-xs text-zinc-600 mt-0.5 truncate">{a.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {a.type === "feedback_overdue" && a.slot_id && (
                    <button
                      onClick={() => onSendChase(a)}
                      disabled={busy}
                      data-testid={`send-chase-${a.candidate_id}`}
                      className="text-[11px] uppercase tracking-[0.15em] bg-zinc-950 text-white px-2.5 py-1.5 rounded-sm hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1"
                    >
                      <PaperPlaneTilt size={11} weight="bold" /> Chase
                    </button>
                  )}
                  <Link
                    to={`/candidates/${a.candidate_id}`}
                    data-testid={`open-candidate-${a.candidate_id}`}
                    className="text-[11px] uppercase tracking-[0.15em] border border-zinc-200 bg-white text-zinc-950 px-2.5 py-1.5 rounded-sm hover:bg-zinc-100 flex items-center gap-1"
                  >
                    Open <ArrowRight size={11} weight="bold" />
                  </Link>
                  {a.dismissable !== false && (
                    <button
                      onClick={() => onDismiss(a.id)}
                      disabled={busy}
                      data-testid={`dismiss-action-${a.id}`}
                      className="text-zinc-400 hover:text-zinc-950 disabled:opacity-40"
                      title="Dismiss for 24h"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh: refreshAuth } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [error, setError] = useState("");
  const [digest, setDigest] = useState(null);
  const [showTarget, setShowTarget] = useState(false);
  const [todayActions, setTodayActions] = useState(null);
  const [busyAction, setBusyAction] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, u, t] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/dashboard/upcoming"),
        api.get("/dashboard/today-actions"),
      ]);
      setStats(s.data);
      setUpcoming(u.data);
      setTodayActions(t.data);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshActions = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/today-actions");
      setTodayActions(data);
    } catch (e) { /* silent */ }
  }, []);

  const dismissAction = async (actionId) => {
    setBusyAction(actionId);
    try {
      // Optimistic remove
      setTodayActions((prev) => prev && ({
        ...prev,
        actions: prev.actions.filter((a) => a.id !== actionId),
        total: Math.max(0, (prev.total || 0) - 1),
      }));
      await api.post(`/dashboard/actions/${encodeURIComponent(actionId)}/dismiss`);
    } catch (e) {
      await refreshActions();
    } finally {
      setBusyAction("");
    }
  };

  const sendChase = async (action) => {
    setBusyAction(action.id);
    try {
      await api.post(`/candidates/${action.candidate_id}/interviews/${action.slot_id}/send-followup`);
      await refreshActions();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setBusyAction("");
    }
  };

  const showDigest = async () => {
    try {
      const { data } = await api.post("/reminders/digest");
      setDigest(data);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  if (error) {
    return <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] p-4 rounded-sm">{error}</div>;
  }
  if (!stats) return <div className="text-zinc-500 text-sm uppercase tracking-[0.2em]">Loading</div>;

  const mine = stats.mine_needs_booking || [];
  const all = stats.needs_booking || [];
  const meRow = (stats.leaderboard || []).find((r) => r.user_id === user?.id);

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Control room</div>
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl leading-none">
            Get them booked out.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={showDigest}
            data-testid="send-digest-btn"
            className="border border-zinc-200 bg-white text-zinc-950 hover:bg-zinc-100 px-4 py-2.5 rounded-sm transition-colors text-sm font-medium flex items-center gap-2"
          >
            <EnvelopeSimple size={14} weight="bold" /> My digest
          </button>
        </div>
      </div>

      {/* Today's Actions — highest priority section */}
      <TodayActionsSection
        data={todayActions}
        busyAction={busyAction}
        onDismiss={dismissAction}
        onSendChase={sendChase}
      />

      {/* Personal target card */}
      {user && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 flex items-center gap-6 flex-wrap" data-testid="my-target-card">
          <div className="flex items-center gap-3 flex-1 min-w-[220px]">
            <div className="w-11 h-11 rounded-sm bg-zinc-950 text-white flex items-center justify-center font-heading font-bold">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your month · {MONTHS[stats.month - 1]}</div>
              <div className="font-heading font-bold text-lg leading-tight">{user.name}</div>
            </div>
          </div>
          <div className="flex-1 min-w-[260px]">
            {meRow ? (
              <TargetProgress
                mtd={meRow.mtd_written}
                target={meRow.monthly_target}
                placements={meRow.placements}
              />
            ) : (
              <div className="text-sm text-zinc-500">No placements this year yet.</div>
            )}
          </div>
          <button
            onClick={() => setShowTarget(true)}
            data-testid="edit-target-btn"
            className="border border-zinc-200 bg-white text-zinc-950 hover:bg-zinc-100 px-3 py-2 rounded-sm transition-colors text-sm font-medium flex items-center gap-1.5"
          >
            <Target size={14} weight="bold" />
            {(user.monthly_target || 0) > 0 ? "Change target" : "Set target"}
          </button>
        </div>
      )}

      {/* Daily reminder banner */}
      {mine.length > 0 && (
        <div className="bg-zinc-950 text-white p-6 rounded-sm" data-testid="daily-reminder-banner">
          <div className="flex items-start gap-4 mb-4">
            <Bell size={22} weight="fill" className="text-[#FF2A2A] flex-shrink-0 mt-1" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-1">Daily reminder — {user?.name}</div>
              <div className="font-heading font-black text-2xl tracking-tight">
                {mine.length} candidate{mine.length === 1 ? "" : "s"} still short of 3 interviews.
              </div>
              <div className="text-sm text-zinc-300 mt-2">Prioritise the red ones — they've been on your books 8+ days.</div>
            </div>
          </div>
          <ul className="space-y-1.5 pl-9" data-testid="daily-reminder-list">
            {mine.slice(0, 6).map((c) => {
              const rem = 3 - c.booked_count;
              return (
                <li key={c.id} className="text-sm text-zinc-200 flex items-center gap-2 flex-wrap">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${AGE_STYLES[c.age_category].dot}`} />
                  <Link to={`/candidates/${c.id}`} className="hover:text-white underline underline-offset-4 decoration-zinc-600 hover:decoration-white">{c.name}</Link>
                  <span className="text-zinc-400">needs</span>
                  <span className="text-white font-medium">{rem} more interview{rem === 1 ? "" : "s"}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="text-zinc-400">{c.days_on_books}d on books</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <KPI label="Active candidates" value={stats.total_active} sub={`${stats.total_closed} closed to date`} icon={Users} testId="kpi-active" />
        <KPI label="Pipeline fee" value={formatFee(stats.pipeline_fee)} sub={`Earned · ${formatFee(stats.placed_fee)}`} icon={CurrencyDollar} testId="kpi-fee" />
        <KPI label="Need interviews" value={stats.needs_booking_count} sub="Short of 3 interviews" icon={Bell} testId="kpi-needs" accent={stats.needs_booking_count > 0} />
        <KPI label="Conversion rate" value={`${stats.conversion_rate}%`} sub={`${stats.total_placed}/${stats.total_closed || 0} closed placed`} icon={Trophy} testId="kpi-conversion" />
      </div>

      {/* Upcoming interviews */}
      <UpcomingInterviews upcoming={upcoming} />

      {/* Outcome folders */}
      <div>
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Outcomes · folders</div>
          <h2 className="font-heading font-bold text-xl tracking-tight">Where every candidate ended up</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4" data-testid="outcome-folders">
          <OutcomeFolder label="Placed" sub="Got the job · fees earned" count={stats.total_placed} fee={stats.placed_fee} accent="emerald" status="placed" testId="folder-placed" />
          <OutcomeFolder label="Countered" sub="Current employer counter-offered" count={stats.total_countered} fee={stats.countered_fee} accent="amber" status="countered" testId="folder-countered" />
          <OutcomeFolder label="Lost" sub="Went elsewhere or withdrew" count={stats.total_lost} fee={stats.lost_fee} accent="red" status="lost" testId="folder-lost" />
        </div>
      </div>

      {/* Team leaderboard */}
      <Leaderboard leaderboard={stats.leaderboard || []} year={stats.year} month={stats.month} currentUserId={user?.id} />

      {/* Age breakdown */}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="aging-breakdown">
        <AgeCard label="Green" sub="Under 3 days on books" count={stats.green} category="green" testId="age-green" />
        <AgeCard label="Yellow" sub="4 – 7 days on books" count={stats.yellow} category="yellow" testId="age-yellow" />
        <AgeCard label="Red — Action now" sub="8+ days · book them today" count={stats.red} category="red" testId="age-red" />
      </div>

      {/* Full priority queue */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Priority queue</div>
            <h2 className="font-heading font-bold text-xl tracking-tight">Candidates that need more interviews</h2>
          </div>
          <Link to="/candidates" className="text-sm text-zinc-500 hover:text-zinc-950 flex items-center gap-1 transition-colors" data-testid="see-all-candidates">
            All candidates <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-200" data-testid="reminders-list">
          {all.length === 0 && (
            <div className="p-8 text-sm text-zinc-500 text-center flex items-center justify-center gap-2">
              <CheckCircle size={16} weight="bold" className="text-emerald-500" />
              Everyone has 3 interviews booked. Nice work.
            </div>
          )}
          {all.map((c) => {
            const st = AGE_STYLES[c.age_category];
            const rem = 3 - c.booked_count;
            return (
              <Link key={c.id} to={`/candidates/${c.id}`} data-testid={`reminder-${c.id}`} className={`flex items-center justify-between p-5 hover:bg-zinc-50 transition-colors ${st.ring}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${st.pill}`}>{c.days_on_books}d</div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">
                      {c.name} <span className="text-zinc-400 font-normal">needs</span> <span className="text-[#B91C1C]">{rem} more interview{rem === 1 ? "" : "s"}</span>
                    </div>
                    <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                      <Clock size={11} weight="bold" />
                      {c.booked_count}/3 booked
                      <span className="text-zinc-300">·</span>
                      {(c.recruiters || []).map((r) => r.name).join(" + ") || "Unassigned"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-xs font-medium hidden sm:block">{formatFee(c.fee_approx)}</div>
                  <ArrowRight size={16} className="text-zinc-400" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {digest && (
        <DigestModal digest={digest} onClose={() => setDigest(null)} />
      )}
      {showTarget && (
        <TargetModal
          user={user}
          current={meRow?.monthly_target || user?.monthly_target || 0}
          onClose={() => setShowTarget(false)}
          onSaved={async () => {
            setShowTarget(false);
            await load();
            if (refreshAuth) await refreshAuth();
          }}
        />
      )}
    </div>
  );
}

function TargetProgress({ mtd, target, placements }) {
  if (!target || target <= 0) {
    return (
      <div>
        <div className="flex items-center justify-between text-sm mb-2">
          <div className="text-zinc-500">Set a monthly target to track progress</div>
          <div className="text-zinc-950 font-medium">MTD · {formatFee(mtd || 0)}</div>
        </div>
        <div className="h-2 bg-zinc-100 rounded-sm" />
      </div>
    );
  }
  const pct = Math.min(100, Math.round(((mtd || 0) / target) * 100));
  const over = (mtd || 0) > target;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-2 gap-2 flex-wrap">
        <div className="text-zinc-950 font-medium">{formatFee(mtd || 0)} <span className="text-zinc-400 font-normal">of {formatFee(target)}</span></div>
        <div className={`text-xs uppercase tracking-[0.15em] font-medium ${over ? "text-emerald-700" : "text-zinc-500"}`}>
          {pct}% · {placements || 0} placement{placements === 1 ? "" : "s"}
        </div>
      </div>
      <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
        <div className={`h-full transition-all ${over ? "bg-emerald-500" : "bg-zinc-950"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function UpcomingInterviews({ upcoming }) {
  return (
    <div data-testid="upcoming-interviews">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Next 7 days</div>
          <h2 className="font-heading font-bold text-xl tracking-tight flex items-center gap-2">
            <Calendar size={20} weight="bold" /> Upcoming interviews
          </h2>
        </div>
        <div className="text-xs text-zinc-500">{upcoming.length} scheduled</div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm">
        {upcoming.length === 0 ? (
          <div className="p-8 text-sm text-zinc-500 text-center">
            No interviews in the next 7 days. Add dates to your interview slots to see them here.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {upcoming.slice(0, 8).map((i) => {
              const dt = new Date(i.scheduled_at);
              const outcomeLabel = i.outcome ? OUTCOME_LABELS[i.outcome] : "";
              return (
                <li key={`${i.candidate_id}-${i.slot_id}`} data-testid={`upcoming-${i.slot_id}`} className="p-4 flex items-center gap-4 hover:bg-zinc-50 transition-colors">
                  <div className="w-14 text-center flex-shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{MONTHS[dt.getMonth()]}</div>
                    <div className="font-heading font-black text-2xl leading-none">{dt.getDate()}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/candidates/${i.candidate_id}`} className="font-medium text-sm hover:underline underline-offset-4">
                      {i.candidate_name}
                    </Link>
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">
                      {i.agency_name}
                      {i.meeting_with ? ` · ${i.meeting_with}` : ""}
                      {i.role ? ` · ${i.role}` : ""}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {outcomeLabel && (
                        <span className="text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border border-zinc-300 text-zinc-700">
                          {outcomeLabel}
                        </span>
                      )}
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${i.invite_sent_client ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-zinc-200 text-zinc-400"}`}>
                        Client {i.invite_sent_client ? "✓" : "…"}
                      </span>
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${i.invite_sent_candidate ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-zinc-200 text-zinc-400"}`}>
                        Cand {i.invite_sent_candidate ? "✓" : "…"}
                      </span>
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-zinc-400 flex-shrink-0" />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

const OUTCOME_LABELS = {
  waiting: "Waiting", offered: "Offered", rejected: "Rejected", no_show: "No-show",
};

function AgeCard({ label, sub, count, category, testId }) {
  const s = AGE_STYLES[category];
  return (
    <div className={`bg-white border border-zinc-200 rounded-sm p-6 ${s.ring}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-700 font-medium">{label}</div>
      </div>
      <div className="font-heading font-black tracking-tight text-5xl leading-none">{count}</div>
      <div className="text-xs text-zinc-500 mt-3">{sub}</div>
    </div>
  );
}

const OUTCOME_STYLES = {
  emerald: { ring: "border-l-4 border-emerald-500", dot: "bg-emerald-500", text: "text-emerald-700" },
  amber: { ring: "border-l-4 border-amber-500", dot: "bg-amber-500", text: "text-amber-700" },
  red: { ring: "border-l-4 border-[#FF2A2A]", dot: "bg-[#FF2A2A]", text: "text-[#B91C1C]" },
};

function OutcomeFolder({ label, sub, count, fee, accent, status, testId }) {
  const s = OUTCOME_STYLES[accent];
  return (
    <Link to={`/candidates?status=${status}`} data-testid={testId} className={`bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-950 transition-colors group ${s.ring}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${s.dot}`} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-700 font-medium">{label}</div>
        </div>
        <FolderOpen size={16} weight="bold" className="text-zinc-400 group-hover:text-zinc-950 transition-colors" />
      </div>
      <div className="font-heading font-black tracking-tight text-5xl leading-none">{count}</div>
      <div className="text-xs text-zinc-500 mt-3">{sub}</div>
      <div className="mt-4 pt-4 border-t border-zinc-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Fees</div>
          <div className={`font-heading font-bold text-lg ${s.text}`}>{formatFee(fee)}</div>
        </div>
        <div className="flex items-center gap-1 text-xs text-zinc-500 group-hover:text-zinc-950 transition-colors">
          Open folder <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

function Leaderboard({ leaderboard, year, month, currentUserId }) {
  const withFees = leaderboard.filter((r) => r.written_fees > 0 || r.placements > 0 || r.monthly_target > 0);
  const nonZero = withFees.length > 0 ? withFees : leaderboard;
  const total = nonZero.reduce((s, r) => s + (r.written_fees || 0), 0);
  const totalMtd = nonZero.reduce((s, r) => s + (r.mtd_written || 0), 0);
  const max = nonZero.reduce((m, r) => Math.max(m, r.written_fees || 0), 0);
  return (
    <div data-testid="leaderboard-section">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Annual written fees · {year}</div>
          <h2 className="font-heading font-bold text-xl tracking-tight">Team leaderboard</h2>
        </div>
        <div className="text-xs text-zinc-500 space-x-3">
          <span>YTD · <span className="text-zinc-950 font-medium">{formatFee(total)}</span></span>
          <span>MTD · <span className="text-zinc-950 font-medium">{formatFee(totalMtd)}</span></span>
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-200" data-testid="leaderboard-list">
        {nonZero.length === 0 ? (
          <div className="p-8 text-sm text-zinc-500 text-center">
            No placements written yet in {year}.
          </div>
        ) : (
          nonZero.map((row, idx) => {
            const pct = max > 0 ? Math.round((row.written_fees / max) * 100) : 0;
            const isMe = row.user_id === currentUserId;
            const isTop = idx === 0 && row.written_fees > 0;
            const targetPct = row.monthly_target > 0 ? Math.min(100, Math.round((row.mtd_written / row.monthly_target) * 100)) : null;
            return (
              <div key={row.user_id} data-testid={`leaderboard-row-${row.user_id}`} className={`p-5 flex items-center gap-4 ${isMe ? "bg-zinc-50" : ""}`}>
                <div className={`w-8 text-center font-heading font-black text-lg ${isTop ? "text-[#FF2A2A]" : "text-zinc-400"}`}>
                  {isTop ? <Crown size={18} weight="fill" className="inline" /> : `#${idx + 1}`}
                </div>
                <div className="w-9 h-9 rounded-sm bg-zinc-950 text-white flex items-center justify-center font-heading font-bold text-sm flex-shrink-0">
                  {row.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium truncate">{row.name || "Unassigned"}</div>
                    {isMe && <span className="text-[10px] uppercase tracking-[0.15em] bg-zinc-950 text-white px-1.5 py-0.5">You</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
                    {row.placements} placement{row.placements === 1 ? "" : "s"} · MTD {formatFee(row.mtd_written)}
                    {row.monthly_target > 0 ? ` of ${formatFee(row.monthly_target)}` : ""}
                  </div>
                  <div className="mt-2 h-1.5 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-zinc-950 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  {targetPct !== null && (
                    <div className="mt-1 h-1 bg-zinc-100 rounded-sm overflow-hidden">
                      <div className={`h-full transition-all ${targetPct >= 100 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${targetPct}%` }} />
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Written YTD</div>
                  <div className="font-heading font-black text-2xl leading-none tracking-tight">{formatFee(row.written_fees)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function DigestModal({ digest, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="digest-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Preview</div>
            <div className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
              <EnvelopeSimple size={20} weight="bold" /> Daily digest
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950"><X size={20} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!digest.email_configured && (
            <div className="border border-amber-500 bg-amber-50 text-amber-900 text-xs px-3 py-2 rounded-sm">
              Email not yet wired. This is the preview of what would be sent daily. Add a Resend key to enable actual email delivery.
            </div>
          )}
          <div className="text-sm">To: <b>{digest.recipient?.name}</b> ({digest.recipient?.email})</div>
          {digest.reminders.length === 0 ? (
            <div className="text-sm text-emerald-700 flex items-center gap-2">
              <CheckCircle size={16} weight="bold" /> All your candidates have 3 interviews booked.
            </div>
          ) : (
            <div>
              <div className="text-sm mb-2">
                You have <b>{digest.reminders.length}</b> candidate{digest.reminders.length === 1 ? "" : "s"} needing more interviews:
              </div>
              <ul className="space-y-2">
                {digest.reminders.map((r) => (
                  <li key={r.candidate_id} className="flex items-center gap-2 text-sm border border-zinc-200 rounded-sm p-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${AGE_STYLES[r.age_category].dot}`} />
                    <span className="flex-1">
                      <b>{r.candidate_name}</b> needs <span className="text-[#B91C1C] font-medium">{r.needs} more</span>
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{r.days_on_books}d</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TargetModal({ current, onClose, onSaved }) {
  const [val, setVal] = useState(current || 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.patch("/users/me/target", { monthly_target: Number(val || 0) });
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/50 flex items-center justify-center p-4" data-testid="target-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Personal</div>
            <div className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
              <Target size={20} weight="bold" /> Monthly target
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-950"><X size={20} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Target (A$ / month)</label>
            <input
              type="number"
              min="0"
              step="500"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              data-testid="target-input"
              autoFocus
              className="w-full bg-white border border-zinc-200 px-3 py-2.5 text-lg font-medium rounded-sm focus:outline-none focus:border-zinc-950"
            />
            <div className="text-xs text-zinc-500 mt-1.5">Placements you write count toward this. Fee splits are proportional.</div>
          </div>
          {error && <div className="border border-[#FF2A2A] bg-red-50 text-[#B91C1C] text-sm px-3 py-2 rounded-sm">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-zinc-200 bg-white text-zinc-950 py-3 rounded-sm font-medium hover:bg-zinc-100 text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-target-btn" className="flex-1 bg-zinc-950 text-white py-3 rounded-sm font-medium hover:bg-zinc-800 text-sm disabled:opacity-50">
              {saving ? "Saving..." : "Save target"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
